import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import {
  ApiError,
  dismissEmployee,
  type EmployeeDetail,
  type EmploymentPeriod,
  fetchBusinessLines,
  fetchDepartments,
  fetchEmployee,
  rehireEmployee,
  searchEmployees,
  updateEmployee,
  type UserProfile,
} from './api'

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="row">
      <span className="label">{label}</span>
      <span className="value">{value || <span className="muted">—</span>}</span>
    </div>
  )
}

function formatDate(value: string | null): string {
  if (!value) return '?'
  const [year, month, day] = value.split('-')
  return `${day}.${month}.${year}`
}

function describePeriod(period: EmploymentPeriod): string {
  const from = `с ${formatDate(period.hired_at)}`
  if (period.left_at === null) return `${from} — работает`
  return `${from} по ${formatDate(period.left_at)}${period.reason ? ` · ${period.reason}` : ''}`
}

/**
 * Увольнение и повторный найм.
 *
 * Удаления карточки нет и не будет: уволенные нужны для динамики численности и остаются
 * в базе навсегда. Со схемы человек исчезает фильтром «показать уволенных», а история
 * найма копится периодами — повторный найм не затирает прошлый уход.
 */
function EmploymentBlock({ person }: { person: EmployeeDetail }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [when, setWhen] = useState('')
  const [reason, setReason] = useState('')
  const [orphaned, setOrphaned] = useState<number | null>(null)

  const done = () => {
    setOpen(false)
    setWhen('')
    setReason('')
    queryClient.invalidateQueries({ queryKey: ['employee', person.id] })
    queryClient.invalidateQueries({ queryKey: ['chart'] })
    queryClient.invalidateQueries({ queryKey: ['departments'] })
    queryClient.invalidateQueries({ queryKey: ['audit'] })
  }

  const dismiss = useMutation({
    mutationFn: () => dismissEmployee(person.id, when, reason),
    onSuccess: (result) => {
      setOrphaned(result.reports_left_without_manager || null)
      done()
    },
  })

  const rehire = useMutation({
    mutationFn: () => rehireEmployee(person.id, when),
    onSuccess: () => {
      setOrphaned(null)
      done()
    },
  })

  const active = dismiss.isPending || rehire.isPending
  const error =
    dismiss.error instanceof ApiError
      ? dismiss.error.message
      : rehire.error instanceof ApiError
        ? rehire.error.message
        : null

  return (
    <div className="employment">
      <span className="label">Занятость</span>

      {person.employment.length === 0 && (
        <p className="muted small">История не велась — карточка пришла из справочника.</p>
      )}
      {person.employment.length > 0 && (
        <ul className="employment-history">
          {person.employment.map((period) => (
            <li key={period.id} className={period.left_at === null ? 'is-open' : undefined}>
              {describePeriod(period)}
            </li>
          ))}
        </ul>
      )}

      {orphaned !== null && (
        <p className="warn">
          Без руководителя осталось подчинённых: {orphaned}. На схеме они висят под
          скрытым узлом — назначьте им нового руководителя.
        </p>
      )}

      {!open && (
        <button type="button" onClick={() => setOpen(true)}>
          {person.is_active ? 'Уволить' : 'Принять обратно'}
        </button>
      )}

      {open && (
        <form
          onSubmit={(event) => {
            event.preventDefault()
            if (person.is_active) dismiss.mutate()
            else rehire.mutate()
          }}
        >
          <label className="field">
            <span>{person.is_active ? 'Дата увольнения' : 'Дата выхода'}</span>
            <input
              type="date"
              value={when}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(event) => setWhen(event.target.value)}
            />
          </label>
          <p className="muted small">Пусто — сегодня. Будущую дату поставить нельзя.</p>

          {person.is_active && (
            <label className="field">
              <span>Причина</span>
              <input value={reason} onChange={(event) => setReason(event.target.value)} />
            </label>
          )}

          {error && <p className="bad form-error">{error}</p>}

          <div className="actions-left">
            <button type="submit" disabled={active}>
              {person.is_active ? 'Уволить' : 'Принять'}
            </button>
            <button type="button" onClick={() => setOpen(false)}>
              Отмена
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

export default function EmployeePanel({
  employeeId,
  onClose,
  currentUser,
}: {
  employeeId: number
  onClose: () => void
  currentUser?: UserProfile
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const employee = useQuery({
    queryKey: ['employee', employeeId],
    queryFn: () => fetchEmployee(employeeId),
  })
  const departments = useQuery({ queryKey: ['departments'], queryFn: fetchDepartments })
  const lines = useQuery({ queryKey: ['business-lines'], queryFn: fetchBusinessLines })

  const [role, setRole] = useState('')
  const [lineId, setLineId] = useState<string>('')
  const [departmentId, setDepartmentId] = useState<string>('')
  const [managerQuery, setManagerQuery] = useState('')
  const [managerId, setManagerId] = useState<number | null>(null)
  const [inManagement, setInManagement] = useState(true)

  useEffect(() => {
    if (!employee.data) return
    setRole(employee.data.role ?? '')
    setLineId(employee.data.business_line_id ? String(employee.data.business_line_id) : '')
    setDepartmentId(employee.data.department_id ? String(employee.data.department_id) : '')
    setManagerId(employee.data.manager?.id ?? null)
    setManagerQuery(employee.data.manager?.display_name ?? '')
    setInManagement(employee.data.in_management_structure)
  }, [employee.data])

  const managerOptions = useQuery({
    queryKey: ['manager-search', managerQuery],
    queryFn: () => searchEmployees(managerQuery),
    enabled: editing && managerQuery.trim().length >= 2,
  })

  const save = useMutation({
    mutationFn: () =>
      updateEmployee(employeeId, {
        role: role || null,
        business_line_id: lineId ? Number(lineId) : null,
        department_id: departmentId ? Number(departmentId) : null,
        manager_id: managerId,
        in_management_structure: inManagement,
      }),
    onSuccess: () => {
      setError(null)
      setEditing(false)
      queryClient.invalidateQueries({ queryKey: ['employee', employeeId] })
      queryClient.invalidateQueries({ queryKey: ['chart'] })
      queryClient.invalidateQueries({ queryKey: ['audit'] })
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить изменения'),
  })

  const person = employee.data
  const isAdmin = currentUser?.role === 'admin'

  return (
    <aside className="side-panel">
      <div className="side-panel-head">
        <strong>{person?.display_name ?? 'Загружаем…'}</strong>
        <button type="button" className="link" onClick={onClose}>
          Закрыть
        </button>
      </div>

      {employee.isError && <p className="bad">Не удалось загрузить карточку</p>}

      {person && !editing && (
        <>
          <Field label="Подразделение" value={person.department} />
          <Field label="Роль" value={person.role} />
          <Field label="Направление" value={person.business_line} />
          <Field label="Руководитель" value={person.manager?.display_name ?? null} />
          <Field
            label="В структуре управления"
            value={person.in_management_structure ? 'да' : 'нет'}
          />
          <Field label="Активен" value={person.is_active ? 'да' : 'нет'} />

          {person.reports.length > 0 && (
            <div className="row">
              <span className="label">Подчинённые</span>
              <span className="value">
                {person.reports.map((report) => report.display_name).join(', ')}
              </span>
            </div>
          )}

          {isAdmin && (
            <button type="button" onClick={() => setEditing(true)}>
              Редактировать
            </button>
          )}

          {isAdmin && <EmploymentBlock person={person} />}
        </>
      )}

      {person && editing && (
        <form
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        >
          <label className="field">
            <span>Роль</span>
            <input value={role} onChange={(event) => setRole(event.target.value)} />
          </label>

          <label className="field">
            <span>Направление</span>
            <select value={lineId} onChange={(event) => setLineId(event.target.value)}>
              <option value="">—</option>
              {(lines.data ?? []).map((line) => (
                <option key={line.id} value={line.id}>
                  {line.name}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Подразделение</span>
            <select
              value={departmentId}
              onChange={(event) => setDepartmentId(event.target.value)}
            >
              <option value="">—</option>
              {(departments.data ?? []).map((department) => (
                <option key={department.id} value={department.id}>
                  {department.code}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Руководитель</span>
            <input
              value={managerQuery}
              placeholder="Начните вводить имя…"
              onChange={(event) => {
                setManagerQuery(event.target.value)
                setManagerId(null)
              }}
            />
          </label>
          {managerOptions.data && managerQuery.trim().length >= 2 && managerId === null && (
            <ul className="search-results inline">
              <li>
                <button
                  type="button"
                  className="link"
                  onClick={() => {
                    setManagerId(null)
                    setManagerQuery('')
                  }}
                >
                  Без руководителя
                </button>
              </li>
              {managerOptions.data.map((candidate) => (
                <li key={candidate.id}>
                  <button
                    type="button"
                    className="link"
                    onClick={() => {
                      setManagerId(candidate.id)
                      setManagerQuery(candidate.display_name)
                    }}
                  >
                    {candidate.display_name}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <label className="toggle">
            <input
              type="checkbox"
              checked={inManagement}
              onChange={(event) => setInManagement(event.target.checked)}
            />
            В активной структуре управления
          </label>

          {error && <p className="bad form-error">{error}</p>}

          <div className="actions-left">
            <button type="submit" disabled={save.isPending}>
              Сохранить
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false)
                setError(null)
              }}
            >
              Отмена
            </button>
          </div>
        </form>
      )}
    </aside>
  )
}
